import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { useSidebar } from "@/components/ui/sidebar";

interface KeepAliveContainerProps extends Omit<HTMLAttributes<HTMLDivElement>, "style"> {
  active: boolean;
  variant: "block" | "contents";
  mobileUnmount?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * CSS-based keep-alive wrapper for route surfaces that should stay mounted.
 *
 * React 19.2's <Activity> preserves React state, but it unmounts effects when
 * hidden. That disconnects and reconnects Convex subscriptions on every tab
 * switch, causing visible flicker and loading states. CSS visibility keeps
 * state, DOM, effects, and subscriptions alive.
 */
export function KeepAliveContainer({
  active,
  variant,
  mobileUnmount = false,
  className,
  children,
  ...props
}: KeepAliveContainerProps) {
  if (mobileUnmount) {
    return (
      <MobileUnmountKeepAliveContainer
        active={active}
        variant={variant}
        className={className}
        {...props}
      >
        {children}
      </MobileUnmountKeepAliveContainer>
    );
  }

  return (
    <KeepAliveContainerContent active={active} variant={variant} className={className} {...props}>
      {children}
    </KeepAliveContainerContent>
  );
}

function MobileUnmountKeepAliveContainer({
  active,
  variant,
  className,
  children,
  ...props
}: Omit<KeepAliveContainerProps, "mobileUnmount">) {
  const { isMobile } = useSidebar();

  if (isMobile && !active) {
    return null;
  }

  return (
    <KeepAliveContainerContent active={active} variant={variant} className={className} {...props}>
      {children}
    </KeepAliveContainerContent>
  );
}

function KeepAliveContainerContent({
  active,
  variant,
  className,
  children,
  ...props
}: Omit<KeepAliveContainerProps, "mobileUnmount">) {
  const style: CSSProperties =
    variant === "contents"
      ? { display: active ? "contents" : "none" }
      : {
          display: active ? undefined : "none",
          position: active ? "relative" : "absolute",
          visibility: active ? "visible" : "hidden",
        };

  return (
    <div
      {...props}
      className={className}
      style={style}
      aria-hidden={!active}
      {...(!active ? { inert: true } : {})}
    >
      {children}
    </div>
  );
}
